// services/ncaaService.js
class NCAAService {
  constructor() {
    this.baseURL = 'https://v1.american-football.api-sports.io';
    this.apiKey = process.env.REACT_APP_FOOTBALL_API_KEY; // Same API key as NFL
    this.debug = false;
    this.cache = {};
    
    if (!this.apiKey) {
      console.warn('🏈 NCAA API key not found in environment variables');
    }
  }

  // Method to manually set API key if env vars aren't working
  setApiKey(key) {
    this.apiKey = key;
    this.log('API key manually set:', { keyLength: key ? key.length : 0 });
  }

  log(message, data = null) {
    if (this.debug) {
      console.log(`[NCAA API] ${message}`, data || ''); 
    }
  }

  // Cache management
  getCacheKey(endpoint, params) {
    return `ncaa_${endpoint}_${JSON.stringify(params)}`;
  }

  setCache(key, data, ttl = 300000) { // 5 minutes default TTL
    this.cache[key] = {
      data,
      timestamp: Date.now(),
      ttl
    };
  }

  getCache(key) {
    const cached = this.cache[key];
    if (!cached) return null;
    
    if (Date.now() - cached.timestamp > cached.ttl) {
      delete this.cache[key];
      return null;
    }
    
    return cached.data;
  }

  clearCache() {
    this.cache = {};
    this.log('Cache cleared');
  }

  async makeRequest(endpoint, params = {}) {
    // Check cache first
    const cacheKey = this.getCacheKey(endpoint, params);
    const cachedData = this.getCache(cacheKey);
    if (cachedData) {
      this.log(`Cache hit for ${endpoint}`, params);
      return cachedData;
    }

    this.log(`Making request to ${endpoint}`, params);
    
    if (!this.apiKey) {
      this.log('API Key Debug Info:', {
        envVar: process.env.REACT_APP_FOOTBALL_API_KEY,
        envVarType: typeof process.env.REACT_APP_FOOTBALL_API_KEY,
        envVarLength: process.env.REACT_APP_FOOTBALL_API_KEY ? process.env.REACT_APP_FOOTBALL_API_KEY.length : 0,
        allReactAppVars: Object.keys(process.env).filter(key => key.startsWith('REACT_APP_'))
      });
      throw new Error(`NCAA API key not configured. 
Environment variable REACT_APP_FOOTBALL_API_KEY is: ${process.env.REACT_APP_FOOTBALL_API_KEY}
Available REACT_APP_ variables: ${Object.keys(process.env).filter(key => key.startsWith('REACT_APP_')).join(', ')}

Please:
1. Ensure your .env file is in the root directory (same level as package.json)
2. Restart your development server after adding the variable
3. Make sure the .env file contains: REACT_APP_FOOTBALL_API_KEY=your_key_here
4. Or use NCAAService.setApiKey('your_key_here') as a temporary workaround`);
    }

    const url = new URL(`${this.baseURL}${endpoint}`);
    
    // Add query parameters
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
        url.searchParams.append(key, params[key]);
      }
    });

    this.log(`Request URL: ${url.toString()}`);

    try {
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'x-rapidapi-host': 'v1.american-football.api-sports.io',
          'x-rapidapi-key': this.apiKey,
          'Content-Type': 'application/json'
        }
      });

      this.log(`Response status: ${response.status}`);

      if (!response.ok) {
        const errorText = await response.text();
        this.log(`Error response:`, errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      this.log(`Response data:`, data);

      // Check for API errors in response
      if (data.errors && data.errors.length > 0) {
        this.log(`API errors:`, data.errors);
        throw new Error(`API Error: ${data.errors.join(', ')}`);
      }

      // Cache successful responses with different TTLs based on data type
      let ttl = 300000; // 5 minutes default
      if (endpoint.includes('/games')) ttl = 30000; // 30 seconds for live games
      if (endpoint.includes('/standings')) ttl = 3600000; // 1 hour for standings
      if (endpoint.includes('/leagues') || endpoint.includes('/seasons')) ttl = 86400000; // 24 hours for static data

      this.setCache(cacheKey, data, ttl);
      return data;
    } catch (error) {
      this.log(`Request failed:`, error.message);
      console.error('NCAA API request failed:', error);
      throw error;
    }
  }

  // CORE ENDPOINTS

  // Get timezone list
  async getTimezones() {
    return await this.makeRequest('/timezone');
  }

  // Get seasons
  async getSeasons() {
    return await this.makeRequest('/seasons');
  }

  // Get leagues/competitions (will filter for NCAA)
  async getLeagues(id = null, season = null, current = null) {
    const params = {};
    if (id) params.id = id;
    if (season) params.season = season;
    if (current) params.current = current;
    
    const response = await this.makeRequest('/leagues', params);
    
    // Filter for NCAA only if no specific id requested
    if (!id && response.response) {
      response.response = response.response.filter(league => league.league.id === 2);
    }
    
    return response;
  }

  // Get teams (defaulting to NCAA)
  async getTeams(id = null, league = 2, season = null, name = null, code = null, search = null) {
    const params = {};
    if (id) params.id = id;
    if (league) params.league = league; // Default to NCAA
    if (season) params.season = season;
    if (name) params.name = name;
    if (code) params.code = code;
    if (search) params.search = search;

    // This endpoint requires at least one parameter
    if (!id && !league && !season && !name && !code && !search) {
      throw new Error('getTeams requires at least one parameter');
    }

    return await this.makeRequest('/teams', params);
  }

  // Get players
  async getPlayers(id = null, name = null, team = null, season = null, search = null) {
    const params = {};
    if (id) params.id = id;
    if (name) params.name = name;
    if (team) params.team = team;
    if (season) params.season = season;
    if (search) params.search = search;

    // This endpoint requires at least one parameter
    if (!id && !name && !team && !season && !search) {
      throw new Error('getPlayers requires at least one parameter');
    }

    return await this.makeRequest('/players', params);
  }

  // Get player statistics
  async getPlayerStatistics(id = null, team = null, season) {
    if (!season) {
      throw new Error('getPlayerStatistics requires season parameter');
    }
    const params = { season };
    if (id) params.id = id;
    if (team) params.team = team;

    // This endpoint requires at least season + (id or team)
    if (!id && !team) {
      throw new Error('getPlayerStatistics requires season and either id or team parameter');
    }

    return await this.makeRequest('/players/statistics', params);
  }

  // Get injuries
  async getInjuries(player = null, team = null) {
    const params = {};
    if (player) params.player = player;
    if (team) params.team = team;

    // This endpoint requires at least one parameter
    if (!player && !team) {
      throw new Error('getInjuries requires either player or team parameter');
    }

    return await this.makeRequest('/injuries', params);
  }

  // GAMES & MATCHES

  // Get games (defaulting to NCAA)
  async getGames(id = null, date = null, league = 2, season = null, team = null, h2h = null, live = null, timezone = null) {
    const params = {};
    if (id) params.id = id;
    if (date) params.date = date;
    if (league) params.league = league; // Default to NCAA
    if (season) params.season = season;
    if (team) params.team = team;
    if (h2h) params.h2h = h2h;
    if (live) params.live = live;
    if (timezone) params.timezone = timezone;

    // This endpoint requires at least one parameter
    if (!id && !date && !league && !season && !team && !h2h && !live) {
      // Default to NCAA league if no other params provided
      params.league = 2;
    }

    return await this.makeRequest('/games', params);
  }

  // Get today's games
  async getTodayGames(timezone = null) {
    const today = new Date().toISOString().split('T')[0];
    return await this.getGames(null, today, 2, null, null, null, null, timezone);
  }

  // Get live games
  async getLiveGames() {
    return await this.getGames(null, null, 2, null, null, null, 'all');
  }

  // Get game events
  async getGameEvents(gameId) {
    if (!gameId) {
      throw new Error('getGameEvents requires gameId parameter');
    }
    return await this.makeRequest('/games/events', { id: gameId });
  }

  // Get game team statistics
  async getGameTeamStatistics(gameId, team = null) {
    if (!gameId) {
      throw new Error('getGameTeamStatistics requires gameId parameter');
    }
    const params = { id: gameId };
    if (team) params.team = team;
    return await this.makeRequest('/games/statistics/teams', params);
  }

  // Get game player statistics
  async getGamePlayerStatistics(gameId, group = null, team = null, player = null) {
    if (!gameId) {
      throw new Error('getGamePlayerStatistics requires gameId parameter');
    }
    const params = { id: gameId };
    if (group) params.group = group;
    if (team) params.team = team;
    if (player) params.player = player;
    return await this.makeRequest('/games/statistics/players', params);
  }

  // STANDINGS

  // Get standings (defaulting to NCAA)
  async getStandings(league = 2, season, team = null, conference = null, division = null) {
    if (!season) {
      throw new Error('getStandings requires season parameter');
    }
    const params = { league: league || 2, season }; // Default to NCAA
    if (team) params.team = team;
    if (conference) params.conference = conference;
    if (division) params.division = division;
    return await this.makeRequest('/standings', params);
  }

  // Get conferences (defaulting to NCAA)
  async getConferences(league = 2, season) {
    if (!season) {
      throw new Error('getConferences requires season parameter');
    }
    return await this.makeRequest('/standings/conferences', { league: league || 2, season });
  }

  // Get divisions (defaulting to NCAA)
  async getDivisions(league = 2, season) {
    if (!season) {
      throw new Error('getDivisions requires season parameter');
    }
    return await this.makeRequest('/standings/divisions', { league: league || 2, season });
  }

  // ODDS

  // Get odds
  async getOdds(game, bookmaker = null, bet = null) {
    if (!game) {
      throw new Error('getOdds requires game parameter');
    }
    const params = { game };
    if (bookmaker) params.bookmaker = bookmaker;
    if (bet) params.bet = bet;
    return await this.makeRequest('/odds', params);
  }

  // Get available bets
  async getBets(id = null, search = null) {
    const params = {};
    if (id) params.id = id;
    if (search) params.search = search;
    return await this.makeRequest('/odds/bets', params);
  }

  // Get bookmakers
  async getBookmakers(id = null, search = null) {
    const params = {};
    if (id) params.id = id;
    if (search) params.search = search;
    return await this.makeRequest('/odds/bookmakers', params);
  }

  // UTILITY FUNCTIONS

  // Get today's date in YYYY-MM-DD format
  getTodayDate() {
    return new Date().toISOString().split('T')[0];
  }

  // Get current season (NCAA seasons span academic years, e.g., 2023 season runs Aug 2023 - Jan 2024)
  getCurrentSeason() {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1; // JavaScript months are 0-indexed
    
    // NCAA season typically starts in August and ends in January of next year
    // So if we're in Jan-July, we're looking at the previous year's season
    return month <= 7 ? year - 1 : year;
  }

  // Get today's date in ISO format
  getTodayDate() {
    return new Date().toISOString().split('T')[0];
  }

  // Get the default date for games based on season status
  getDefaultGameDate() {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    
    // If we're in the off-season (February-July), show the upcoming season opener
    if (month >= 2 && month <= 7) {
      // NCAA season typically starts last week of August
      return `${year}-08-26`;
    }
    
    // Otherwise, show today's date
    return this.getTodayDate();
  }

  // Get games for multiple seasons
  async getGamesForSeasons(date = null, seasons = [2025, 2024, 2023], league = 2, team = null, timezone = 'America/New_York') {
    try {
      const allGames = [];
      const errors = [];
      
      // Determine which seasons to query based on the date
      const seasonsToQuery = date ? seasons : [this.getCurrentSeason()];
      
      // Query each season
      for (const season of seasonsToQuery) {
        try {
          const response = await this.getGames(
            null, // id
            date, // date
            league, // league (NCAA = 2)
            season, // season
            team, // team
            null, // h2h
            null, // live
            timezone // timezone
          );
          
          if (response && response.response) {
            allGames.push(...response.response);
          }
        } catch (error) {
          console.warn(`Failed to load NCAA games for season ${season}:`, error);
          errors.push({ season, error: error.message });
        }
      }
      
      // Deduplicate games by game ID
      const uniqueGames = allGames.reduce((acc, game) => {
        const gameId = game.game?.id || game.id;
        if (gameId && !acc.some(g => (g.game?.id || g.id) === gameId)) {
          acc.push(game);
        }
        return acc;
      }, []);
      
      // Sort by game time
      uniqueGames.sort((a, b) => {
        const dateA = new Date(a.game?.date?.date + 'T' + a.game?.date?.time || a.game?.date || 0);
        const dateB = new Date(b.game?.date?.date + 'T' + b.game?.date?.time || b.game?.date || 0);
        return dateA - dateB;
      });
      
      return {
        response: uniqueGames,
        errors: errors.length > 0 ? errors : null,
        results: uniqueGames.length
      };
    } catch (error) {
      console.error('Failed to load NCAA games for multiple seasons:', error);
      return {
        response: [],
        errors: [{ error: error.message }],
        results: 0
      };
    }
  }

  // Get available seasons for current era (2022-2025)
  getAvailableSeasons() {
    return [2025, 2024, 2023, 2022, 2021];
  }

  // Format time for display
  formatTime(dateTime) {
    if (!dateTime) return 'TBD';
    
    try {
      const date = new Date(dateTime);
      if (isNaN(date.getTime())) return 'TBD';
      
      const options = { 
        hour: 'numeric', 
        minute: '2-digit', 
        hour12: true,
        timeZone: 'America/New_York'
      };
      
      return date.toLocaleTimeString('en-US', options) + ' ET';
    } catch (error) {
      return 'TBD';
    }
  }

  // Get full date time display
  getFullDateTime(dateTime) {
    if (!dateTime) return 'Date TBD';
    
    try {
      const date = new Date(dateTime);
      if (isNaN(date.getTime())) return 'Date TBD';
      
      const dateOptions = { 
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        timeZone: 'America/New_York'
      };
      
      const timeOptions = { 
        hour: 'numeric', 
        minute: '2-digit', 
        hour12: true,
        timeZone: 'America/New_York'
      };
      
      const dateStr = date.toLocaleDateString('en-US', dateOptions);
      const timeStr = date.toLocaleTimeString('en-US', timeOptions) + ' ET';
      
      return `${dateStr} at ${timeStr}`;
    } catch (error) {
      return 'Date TBD';
    }
  }

  // Get status color based on game status
  getStatusColor(status) {
    const statusMap = {
      'NS': 'bg-gray-500/30 text-gray-300',      // Not Started
      'IN PLAY': 'bg-red-500/30 text-red-300',   // In Progress
      'LIVE': 'bg-red-500/30 text-red-300',      // Live
      'HT': 'bg-orange-500/30 text-orange-300',  // Half Time
      'FT': 'bg-green-500/30 text-green-300',    // Full Time / Finished
      'AOT': 'bg-green-500/30 text-green-300',   // After Overtime
      'PST': 'bg-yellow-500/30 text-yellow-300', // Postponed
      'CANC': 'bg-gray-500/30 text-gray-300',    // Cancelled
      'INT': 'bg-orange-500/30 text-orange-300', // Interrupted
      'ABD': 'bg-gray-500/30 text-gray-300',     // Abandoned
    };
    
    return statusMap[status] || 'bg-gray-500/30 text-gray-300';
  }

  // Get conference display name
  getConferenceName(conference) {
    if (!conference) return 'Unknown';
    
    // Handle common abbreviations
    const conferenceMap = {
      'ACC': 'Atlantic Coast',
      'AAC': 'American Athletic',
      'B12': 'Big 12',
      'B10': 'Big Ten',
      'CUSA': 'Conference USA',
      'MAC': 'Mid-American',
      'MWC': 'Mountain West',
      'PAC': 'Pacific-12',
      'SEC': 'Southeastern',
      'SBC': 'Sun Belt',
      'Independent': 'FBS Independents'
    };
    
    // Check if it's an abbreviation
    const mapped = conferenceMap[conference];
    if (mapped) return mapped;
    
    // Otherwise return as-is
    return conference;
  }

  // Get conferences for a league and season
  async getConferences(league = 2, season) {
    try {
      // For NCAA, we'll get this from standings
      const standings = await this.getStandings(league, season);
      if (!standings || !standings.response) return { response: [] };
      
      const conferences = [...new Set(standings.response.map(team => team.conference || team.group))].filter(Boolean);
      return { response: conferences.sort() };
    } catch (error) {
      console.error('Failed to get conferences:', error);
      return { response: [] };
    }
  }

  // Get divisions for a league and season (NCAA doesn't really have divisions like NFL)
  async getDivisions(league = 2, season) {
    try {
      // NCAA uses conferences instead of divisions
      return await this.getConferences(league, season);
    } catch (error) {
      console.error('Failed to get divisions:', error);
      return { response: [] };
    }
  }

  // Get date range
  getDateRange(days = 7, startFromToday = true) {
    const dates = [];
    const startOffset = startFromToday ? 0 : -days;
    
    for (let i = startOffset; i < (startFromToday ? days : 0); i++) {
      const date = new Date();
      date.setDate(date.getDate() + i);
      dates.push(date.toISOString().split('T')[0]);
    }
    return dates;
  }

  // Get upcoming dates with labels
  getUpcomingDates(days = 7) {
    const dates = [];
    for (let i = 0; i < days; i++) {
      const date = new Date();
      date.setDate(date.getDate() + i);
      const dateStr = date.toISOString().split('T')[0];
      
      let label;
      if (i === 0) label = 'Today';
      else if (i === 1) label = 'Tomorrow';
      else label = date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
      
      dates.push({ value: dateStr, label });
    }
    return dates;
  }

  // Format time with timezone
  formatTime(dateString, timezone = 'America/New_York') {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  }

  // Format date
  formatDate(dateString, timezone = 'America/New_York') {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      timeZone: timezone,
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  }

  // Get full date time
  getFullDateTime(dateString, timezone = 'America/New_York') {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      timeZone: timezone,
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  }

  // Get status color for games
  getStatusColor(status) {
    const statusMap = {
      'NS': 'bg-gray-100 text-gray-800',       // Not Started
      'Q1': 'bg-green-100 text-green-800',     // First Quarter (In Play)
      'Q2': 'bg-green-100 text-green-800',     // Second Quarter (In Play)
      'Q3': 'bg-green-100 text-green-800',     // Third Quarter (In Play)
      'Q4': 'bg-green-100 text-green-800',     // Fourth Quarter (In Play)
      'OT': 'bg-yellow-100 text-yellow-800',   // Overtime (In Play)
      'HT': 'bg-blue-100 text-blue-800',       // Halftime (In Play)
      'FT': 'bg-slate-100 text-slate-800',     // Finished (Game Finished)
      'AOT': 'bg-slate-100 text-slate-800',    // After Over Time (Game Finished)
      'CANC': 'bg-red-100 text-red-800',       // Cancelled
      'PST': 'bg-yellow-100 text-yellow-800'   // Postponed
    };
    return statusMap[status] || 'bg-gray-100 text-gray-800';
  }

  // Get conference/division display name (NCAA specific)
  getConferenceName(conference) {
    const conferenceMap = {
      'Atlantic Coast': 'ACC',
      'American Athletic': 'AAC',
      'Big 12': 'Big 12',
      'Big Ten': 'Big Ten',
      'Conference USA': 'C-USA',
      'FBS Independents': 'Independent',
      'Mid-American': 'MAC',
      'Mountain West': 'MW',
      'Pacific-12': 'Pac-12',
      'Southeastern Conference': 'SEC',
      'Sun Belt': 'Sun Belt',
      'Big Sky': 'Big Sky',
      'Big South': 'Big South',
      'Colonial Athletic Association': 'CAA',
      'Ivy League': 'Ivy',
      'Mid-Eastern Athletic': 'MEAC',
      'Northeast': 'NEC',
      'Missouri Valley': 'MVFC',
      'Ohio Valley': 'OVC',
      'Patriot League': 'Patriot',
      'Pioneer': 'Pioneer',
      'Southern': 'SoCon',
      'Southland': 'Southland',
      'Southwestern Athletic': 'SWAC'
    };
    return conferenceMap[conference] || conference;
  }

  // Get division display name (NCAA specific)
  getDivisionName(division) {
    return division; // NCAA divisions are usually already short (Atlantic, Coastal, etc.)
  }

  // Test API connection
  async testConnection() {
    try {
      this.log('Testing API connection...');
      const timezonesResult = await this.getTimezones();
      this.log('Timezones test result:', timezonesResult);
      
      if (timezonesResult && timezonesResult.response) {
        this.log('✅ API connection successful');
        return { success: true, timezones: timezonesResult.response.slice(0, 10) };
      } else {
        throw new Error('Invalid response structure');
      }
    } catch (error) {
      this.log('❌ API connection failed:', error.message);
      return { success: false, error: error.message };
    }
  }

  // Get sample data for testing
  async getSampleData() {
    try {
      this.log('Getting sample data...');
      
      // Try to get seasons first
      const seasons = await this.getSeasons();
      this.log('Sample seasons:', seasons);
      
      // Try to get NCAA league
      const leagues = await this.getLeagues(2); // NCAA
      this.log('Sample leagues:', leagues);
      
      // Try to get current NCAA teams
      const currentSeason = this.getCurrentSeason();
      const ncaaTeams = await this.getTeams(null, 2, currentSeason); // NCAA = league 2
      this.log('Sample NCAA teams:', ncaaTeams);
      
      return {
        seasons: seasons.response?.slice(-4) || [], // Last 4 seasons
        leagues: leagues.response || [],
        teams: ncaaTeams.response?.slice(0, 20) || [],
        currentSeason
      };
      
    } catch (error) {
      this.log('Error getting sample data:', error.message);
      throw error;
    }
  }

  // Debug method to check environment setup
  debugEnvironment() {
    const envInfo = {
      nodeEnv: process.env.NODE_ENV,
      apiKey: this.apiKey ? `Set (${this.apiKey.length} chars)` : 'Not set',
      apiKeyRaw: process.env.REACT_APP_FOOTBALL_API_KEY,
      allReactAppVars: Object.keys(process.env).filter(key => key.startsWith('REACT_APP_')),
      envCount: Object.keys(process.env).length,
      cacheSize: Object.keys(this.cache).length,
      currentSeason: this.getCurrentSeason()
    };
    
    console.log('🏈 NCAA Service Environment Debug:', envInfo);
    return envInfo;
  }
}

// Add a global method for easy testing in console
window.NCAAService = new NCAAService();

export default window.NCAAService;